import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getAdminStats, type AdminStats, type AdminStatsRange } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Activity,
  ChevronDown,
  Eye,
  Flag,
  Globe,
  UserPlus,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/admin/")({
  component: AdminOverview,
});

const RANGE_LABELS: Record<AdminStatsRange, string> = {
  today: "today",
  "7d": "last 7 days",
  "30d": "last 30 days",
  "90d": "last 90 days",
  "365d": "last year",
  all: "all time",
};

function AdminOverview() {
  const [range, setRange] = useState<AdminStatsRange>("30d");

  const { data: stats, isLoading, isFetching } = useQuery({
    queryKey: ["admin-stats", range],
    queryFn: async () => {
      const res = await getAdminStats({ range });
      if (!res.ok) throw new Error("failed to load stats");
      return (await res.json()) as AdminStats;
    },
    refetchInterval: 60_000,
    // keep the current numbers on screen while a new range loads
    placeholderData: keepPreviousData,
  });

  // dim (don't blank) while a range change is in flight
  const busy = isFetching && !isLoading;

  const statCards = [
    {
      label: "users online",
      value: stats?.online,
      hint: "active in the last 15 min",
      icon: Activity,
    },
    {
      label: `registered ${RANGE_LABELS[range]}`,
      value: stats?.range.registered,
      hint: `${stats?.totals.users ?? "—"} users total`,
      icon: UserPlus,
    },
    {
      label: "total users",
      value: stats?.totals.users,
      icon: Users,
    },
    {
      label: "pages registered",
      value: stats?.totals.pages,
      hint: `${stats?.totals.publishedPages ?? "—"} published`,
      icon: Globe,
    },
    {
      label: "total site views",
      value: stats?.totals.views,
      hint: `${stats?.totals.uniqueVisits ?? "—"} unique visits`,
      icon: Eye,
    },
    {
      label: "pending reports",
      value: stats?.totals.pendingReports,
      icon: Flag,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">overview</h2>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-2xl gap-2">
              {RANGE_LABELS[range]}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(Object.keys(RANGE_LABELS) as AdminStatsRange[]).map((key) => (
              <DropdownMenuItem key={key} onClick={() => setRange(key)}>
                {RANGE_LABELS[key]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div
        className={`grid grid-cols-2 gap-4 md:grid-cols-3 transition-opacity duration-200 ${
          busy ? "opacity-60" : ""
        }`}
      >
        {statCards.map((s) => (
          <Card
            key={s.label}
            className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <s.icon className="h-4 w-4 text-amber-600" />
                {s.label}
              </div>
              <div className="mt-2 text-3xl font-bold text-gray-900 tabular-nums">
                {isLoading || s.value === undefined ? (
                  <span className="inline-block h-8 w-16 bg-amber-100/60 rounded animate-pulse" />
                ) : (
                  s.value.toLocaleString()
                )}
              </div>
              {s.hint && <div className="mt-1 text-xs text-gray-500">{s.hint}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div
        className={`grid gap-4 lg:grid-cols-2 transition-opacity duration-200 ${
          busy ? "opacity-60" : ""
        }`}
      >
        <ChartCard
          title="registrations per day"
          subtitle={RANGE_LABELS[range]}
          data={stats?.registrationsSeries}
          loading={isLoading}
        />
        <ChartCard
          title="unique page views per day"
          subtitle={RANGE_LABELS[range]}
          data={stats?.viewsSeries}
          loading={isLoading}
        />
      </div>
    </div>
  );
}

type SeriesPoint = { day: string; count: number };

// fill missing days with zeros so the time axis is continuous
function fillSeries(data: SeriesPoint[]): SeriesPoint[] {
  if (data.length < 2) return data;
  const out: SeriesPoint[] = [];
  const byDay = new Map(data.map((d) => [d.day, d.count]));
  const start = new Date(`${data[0].day}T00:00:00Z`);
  const end = new Date(`${data[data.length - 1].day}T00:00:00Z`);
  for (let t = start.getTime(); t <= end.getTime(); t += 86_400_000) {
    const day = new Date(t).toISOString().slice(0, 10);
    out.push({ day, count: byDay.get(day) ?? 0 });
    if (out.length > 1000) break;
  }
  return out;
}

function ChartCard({
  title,
  subtitle,
  data,
  loading,
}: {
  title: string;
  subtitle: string;
  data?: SeriesPoint[];
  loading: boolean;
}) {
  const filled = useMemo(() => fillSeries(data ?? []), [data]);
  const [hover, setHover] = useState<number | null>(null);

  const W = 600;
  const H = 180;
  const PAD_L = 36;
  const PAD_B = 22;
  const PAD_T = 10;
  const plotW = W - PAD_L - 8;
  const plotH = H - PAD_T - PAD_B;
  const max = Math.max(1, ...filled.map((d) => d.count));
  const barW = filled.length ? Math.max(1, plotW / filled.length - 2) : 0;

  const fmtDay = (day: string) => {
    const d = new Date(`${day}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  // rounded top corners only, anchored to the baseline
  const barPath = (x: number, y: number, w: number, h: number) => {
    const r = Math.min(4, w / 2, h);
    return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
  };

  const hovered = hover !== null ? filled[hover] : null;

  return (
    <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
      <CardHeader className="pb-0">
        <div className="flex items-baseline justify-between">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <span className="text-xs text-gray-500">{subtitle}</span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {loading ? (
          <div className="h-[180px] bg-amber-100/40 rounded-xl animate-pulse" />
        ) : filled.length === 0 ? (
          <div className="h-[180px] flex items-center justify-center text-sm text-gray-500">
            no data in this range
          </div>
        ) : (
          <div className="relative">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full h-auto"
              role="img"
              aria-label={title}
              onMouseLeave={() => setHover(null)}
            >
              {[0, 0.5, 1].map((f) => {
                const y = PAD_T + plotH - plotH * f;
                return (
                  <g key={f}>
                    <line
                      x1={PAD_L}
                      x2={W - 8}
                      y1={y}
                      y2={y}
                      stroke="#e5e7eb"
                      strokeWidth={1}
                    />
                    <text
                      x={PAD_L - 6}
                      y={y + 3}
                      textAnchor="end"
                      fontSize={10}
                      fill="#6b7280"
                    >
                      {Math.round(max * f).toLocaleString()}
                    </text>
                  </g>
                );
              })}
              {filled.map((d, i) => {
                const x = PAD_L + (plotW / filled.length) * i + 1;
                const h = Math.max(d.count > 0 ? 2 : 0, (d.count / max) * plotH);
                const y = PAD_T + plotH - h;
                return (
                  <g key={d.day}>
                    {/* hit target wider than the bar itself */}
                    <rect
                      x={x - 1}
                      y={PAD_T}
                      width={barW + 2}
                      height={plotH}
                      fill="transparent"
                      onMouseEnter={() => setHover(i)}
                    />
                    {h > 0 && (
                      <path
                        d={barPath(x, y, barW, h)}
                        fill={hover === i ? "#d97706" : "#f59e0b"}
                        pointerEvents="none"
                      />
                    )}
                  </g>
                );
              })}
              <line
                x1={PAD_L}
                x2={W - 8}
                y1={PAD_T + plotH}
                y2={PAD_T + plotH}
                stroke="#d1d5db"
                strokeWidth={1}
              />
              {filled.length > 1 &&
                [0, Math.floor((filled.length - 1) / 2), filled.length - 1]
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .map((i) => (
                    <text
                      key={i}
                      x={PAD_L + (plotW / filled.length) * i + barW / 2}
                      y={H - 6}
                      textAnchor={i === 0 ? "start" : i === filled.length - 1 ? "end" : "middle"}
                      fontSize={10}
                      fill="#6b7280"
                    >
                      {fmtDay(filled[i].day)}
                    </text>
                  ))}
            </svg>
            {hovered && hover !== null && (
              <div
                className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-amber-200 bg-white px-2 py-1 text-xs shadow-md"
                style={{
                  left: `${(((PAD_L + (plotW / filled.length) * hover + barW / 2) / W) * 100).toFixed(2)}%`,
                  top: 0,
                }}
              >
                <div className="font-medium text-gray-900 tabular-nums">
                  {hovered.count.toLocaleString()}
                </div>
                <div className="text-gray-500">{fmtDay(hovered.day)}</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
